import * as React from "react";
import { createPortal } from "react-dom";
import { Building2, ChevronDown } from "lucide-react";
import { normalizeAvatarUrl } from "../../lib/profile-avatar-image";
import * as styles from "./AddUserToOrganizationModal.css";

export type InvitableOrganization = {
  id: string;
  name: string;
  logoUrl?: string | null;
};

function OrganizationLogo({
  logoUrl,
  name,
}: {
  logoUrl?: string | null;
  name: string;
}) {
  const src = logoUrl ? normalizeAvatarUrl(logoUrl) ?? logoUrl : null;

  return (
    <div className={styles.orgLogo} aria-hidden>
      {src ? (
        <img src={src} alt="" className={styles.orgLogoImg} />
      ) : (
        <Building2 size={14} color="var(--color-fgMuted, #9CA3AF)" />
      )}
    </div>
  );
}

export function OrganizationPickerSelect({
  label,
  value,
  onChange,
  organizations,
  disabled = false,
  placeholder = "Select an organization",
}: {
  label: string;
  value: string;
  onChange: (orgId: string) => void;
  organizations: InvitableOrganization[];
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const selected = organizations.find((org) => org.id === value);
  const emptyLabel =
    organizations.length === 0 ? "No organizations available" : placeholder;

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || !open) return;
    const rect = trigger.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const run = () => requestAnimationFrame(updatePosition);
    run();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside, true);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [open]);

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <button
        ref={triggerRef}
        type="button"
        className={styles.pickerTrigger}
        disabled={disabled || organizations.length === 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {selected ? (
          <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
            <OrganizationLogo logoUrl={selected.logoUrl} name={selected.name} />
            <span className={styles.orgName}>{selected.name}</span>
          </span>
        ) : (
          <span className={styles.pickerTriggerPlaceholder}>{emptyLabel}</span>
        )}
        <ChevronDown size={16} className={styles.pickerChevron} aria-hidden />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.pickerDropdown}
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
            }}
            role="listbox"
            aria-label={label}
          >
            {organizations.map((org) => (
              <button
                key={org.id}
                type="button"
                role="option"
                aria-selected={org.id === value}
                data-selected={org.id === value ? "true" : "false"}
                className={styles.pickerOption}
                onClick={() => {
                  onChange(org.id);
                  setOpen(false);
                }}
              >
                <OrganizationLogo logoUrl={org.logoUrl} name={org.name} />
                <span className={styles.orgName}>{org.name}</span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
